import { getImageMeta } from '@/utils';
import { PlusOutlined } from '@ant-design/icons';
import { App, Upload } from 'antd';
import { get } from 'lodash';
import mime from 'mime';
import { useTranslation } from 'react-i18next';

/**
 * APIs
 */
import { attachmentsPresigned, cAttachment } from '@/apis/attachment';

/**
 * Types
 */
import type { PresignedResDto } from '@/dtos/attachment.dto';
import type { UploadProps } from 'antd';
import type { RcFile } from 'antd/es/upload';

type Props = UploadProps & {
    'aria-invalid'?: boolean;
    app: string;
    module: string;
};

const ComponentUpload: React.FC<Props> = (props) => {
    /**
     * Hooks
     */
    const { message } = App.useApp();

    const { t } = useTranslation();

    /**
     * ChildrenProps
     */
    const uploadProps: UploadProps = {
        className: props['aria-invalid'] ? 'upload-base upload-error' : 'upload-base',
        fileList: props.fileList,
        listType: 'picture-card',
        maxCount: props.maxCount || 1,
        showUploadList: {
            showPreviewIcon: false,
        },
        beforeUpload: async (file: RcFile & { data?: PresignedResDto }) => {
            // 生成预签名 URL
            const { code, data } = await attachmentsPresigned({
                extension: mime.getExtension(file.type) || 'unknown',
                options: {
                    Method: 'PUT',
                },
            });

            file.data = data;

            return code === 0;
        },
        customRequest: (options) => {
            const file = options.file as RcFile & { data: PresignedResDto };

            fetch(file.data.url, {
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
                method: 'PUT',
            })
                .then(async (res) => {
                    if (res.status !== 200) {
                        return message.error(res.statusText);
                    }

                    const imageMeta = await getImageMeta(file);

                    cAttachment({
                        app: props.app,
                        extension: mime.getExtension(file.type) || file.name.split('.').pop() || 'unknown',
                        height: imageMeta.height,
                        mime_type: file.type,
                        module: props.module,
                        name: file.name,
                        path: file.data.path,
                        size: file.size,
                        type: 'image',
                        width: imageMeta.width,
                    })
                        .then(({ data }) => {
                            file.data.path = data.path;
                            file.data.url = `${import.meta.env.VITE_CDN}/${data.path}`;

                            options.onSuccess?.(res);
                        })
                        .catch(options.onError);
                })
                .catch(options.onError);
        },
        onChange: props.onChange,
    };

    return (
        <Upload {...uploadProps}>
            {get(props, 'fileList.length', 0) < uploadProps.maxCount! && (
                <div>
                    <PlusOutlined />

                    <div style={{ marginTop: 8 }}>{t('upload')}</div>
                </div>
            )}
        </Upload>
    );
};

export default ComponentUpload;
